import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import { Building2, CheckCircle2, Clock, Loader2, RefreshCw, XCircle } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";
import { getCurrentTenant } from "@eduviaos/shared";
import type { PublicTrackResponse } from "@eduviaos/shared/types";
import { trackEnquiry } from "@/lib/api";
import { parsePublicError } from "@/lib/error-utils";
import { PageCard } from "@/components/AppFrame";
import { ErrorState } from "@/components/ErrorState";

/**
 * Parent-facing tracker. The tracker token comes from the URL path
 * (`/track/:token`); the tenant slug comes from the host name (D2d).
 *
 * Refetches on a 60s timer so a parent who leaves the tab open sees
 * status changes without manually reloading. We deliberately do NOT
 * tie this to `refetchOnWindowFocus` because the BE response carries
 * Cache-Control: no-store and the focus-refetch pattern is for
 * authenticated SPAs where the tab is always "live"; parents are more
 * likely to reopen the bookmark hours later.
 */
export function TrackPage() {
  const tenant = React.useMemo(() => getCurrentTenant(), []);
  const tenantSlug = tenant.slug ?? "";
  const { token = "" } = useParams<{ token: string }>();

  const query = useQuery({
    queryKey: ["public-track", tenantSlug, token],
    queryFn: () => trackEnquiry(token),
    enabled: !!tenantSlug && !!token,
    refetchInterval: 60_000,
  });

  if (!tenantSlug) {
    return (
      <ErrorState
        title="Open your institution's tracker link"
        body={
          <>
            Trackers live on each institution's apply surface. Open the tracking link the
            institution sent you (it'll be on a host like{" "}
            <code className="bg-muted/40 rounded px-1 py-0.5 font-mono text-xs">
              acme-school--apply.eduviaos.com
            </code>
            ).
          </>
        }
        cta={
          <Button asChild variant="outline">
            <Link to="/welcome">About EduViaOS</Link>
          </Button>
        }
      />
    );
  }

  if (!token) {
    return (
      <ErrorState
        title="Missing tracker token"
        body="The tracker URL needs to include a token. Open the link the institution emailed you."
        cta={
          <Button asChild variant="outline">
            <Link to="/">Back to enquiry form</Link>
          </Button>
        }
      />
    );
  }

  if (query.isLoading) {
    return (
      <PageCard>
        <div
          role="status"
          aria-live="polite"
          className="text-muted-foreground flex items-center gap-2 py-6 text-sm"
        >
          <Loader2 className="text-primary h-4 w-4 animate-spin" />
          <span>Loading enquiry status…</span>
        </div>
      </PageCard>
    );
  }

  if (query.error) {
    const parsed = parsePublicError(query.error);
    if (parsed.kind === "tenant_or_form_not_found") {
      return (
        <ErrorState
          title="Tracker link not recognised"
          body={
            <>
              We couldn't find an enquiry with this link. The link may have been mistyped, may
              belong to a different institution, or may have been revoked. Please double-check the
              URL or contact the institution directly.
            </>
          }
          cta={
            <Button asChild variant="outline">
              <Link to="/">Back to home</Link>
            </Button>
          }
        />
      );
    }
    return (
      <ErrorState
        title="Couldn't load enquiry status"
        body={parsed.message}
        cta={<Button onClick={() => query.refetch()}>Try again</Button>}
      />
    );
  }

  const data = query.data;
  if (!data) return null;

  return <TrackerView data={data} onRefresh={() => query.refetch()} fetching={query.isFetching} />;
}

// ── Sub-components ──────────────────────────────────────────────────────

function TrackerView({
  data,
  onRefresh,
  fetching,
}: {
  data: PublicTrackResponse;
  onRefresh: () => void;
  fetching: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.32, 0.72, 0.32, 1] }}
      className="grid gap-4"
    >
      <header className="border-border/60 bg-card/60 flex items-center gap-3 rounded-xl border p-4 shadow-sm">
        <span
          aria-hidden
          className="bg-primary/10 text-primary flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
        >
          <Building2 className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-foreground truncate text-base font-semibold leading-tight">
            {data.tenant_name}
          </div>
          <div className="text-muted-foreground truncate text-xs">
            Enquiry for {data.student_name}
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={fetching}
          aria-label="Refresh status"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${fetching ? "animate-spin" : ""}`} />
        </Button>
      </header>

      <PageCard>
        <CurrentStageBanner
          label={data.stage_label}
          colorHint={data.stage_color_hint}
          isTerminal={data.is_terminal}
          terminalOutcome={data.terminal_outcome}
        />

        <div className="mt-6">
          <h2 className="text-foreground mb-3 text-sm font-semibold tracking-tight">
            Progress timeline
          </h2>
          <Timeline entries={data.history} submittedAt={data.submitted_at} />
        </div>
      </PageCard>

      <p className="text-muted-foreground text-center text-xs">
        Status auto-refreshes every minute. Last update: {formatTime(new Date().toISOString())}
      </p>
    </motion.div>
  );
}

function CurrentStageBanner({
  label,
  colorHint,
  isTerminal,
  terminalOutcome,
}: {
  label: string;
  colorHint: string | null;
  isTerminal: boolean;
  terminalOutcome: string | null;
}) {
  const tone =
    isTerminal && terminalOutcome === "admitted"
      ? "success"
      : isTerminal && terminalOutcome === "rejected"
        ? "danger"
        : "info";

  const Icon = tone === "success" ? CheckCircle2 : tone === "danger" ? XCircle : Clock;

  const toneClasses =
    tone === "success"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
      : tone === "danger"
        ? "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300"
        : "border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300";

  return (
    <div
      className={`flex flex-col gap-2 rounded-xl border p-4 sm:flex-row sm:items-center sm:gap-4 ${toneClasses}`}
      // Custom color hint, when present, paints a 4px accent bar on
      // the left so even within the same tone family different stages
      // visually differ — useful for a 5-stage funnel where 3 stages
      // share the "info" tone.
      style={
        colorHint && !isTerminal ? { borderLeftWidth: 4, borderLeftColor: colorHint } : undefined
      }
    >
      <span aria-hidden className="flex h-8 w-8 items-center justify-center">
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide opacity-80">Current stage</div>
        <div className="text-base font-semibold leading-tight">{label}</div>
        {isTerminal && terminalOutcome && (
          <div className="mt-0.5 text-xs opacity-90">
            Final outcome: <span className="font-medium capitalize">{terminalOutcome}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Timeline({
  entries,
  submittedAt,
}: {
  entries: PublicTrackResponse["history"];
  submittedAt: string;
}) {
  // Combine submission as the first entry (always shown) with the
  // history. Sort defensively in case the BE returns out-of-order.
  const items = React.useMemo(() => {
    const submission = {
      to_stage_key: "__submitted__",
      to_stage_label: "Enquiry submitted",
      at: submittedAt,
    };
    return [submission, ...entries].sort(
      (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime(),
    );
  }, [entries, submittedAt]);

  return (
    <ol className="border-border/60 grid gap-3 border-l pl-5">
      {items.map((it, idx) => {
        const isLast = idx === items.length - 1;
        return (
          <li key={`${it.to_stage_key}-${it.at}`} className="relative">
            <span
              aria-hidden
              className={`bg-background border-border absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 ${
                isLast ? "border-primary" : "border-muted-foreground/40"
              }`}
            />
            <div className="text-foreground text-sm font-medium">{it.to_stage_label}</div>
            <div className="text-muted-foreground text-xs">{formatTime(it.at)}</div>
          </li>
        );
      })}
    </ol>
  );
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
